// Gerador de QR Code (modo byte) — implementação própria, sem dependência externa.
// Usado para exibir o link da ficha na tela do tablet: o parceiro escaneia com o
// próprio celular e envia a foto do documento por lá (a câmera do tablet trava).
//
// qrMatrix(texto) → matriz booleana [linha][coluna]; true = módulo escuro.

const ECC_CODEWORDS = {
  L: [-1, 7, 10, 15, 20, 26, 18, 20, 24, 30, 18, 20, 24, 26, 30, 22, 24, 28, 30, 28, 28, 28, 28, 30, 30, 26, 28, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30],
  M: [-1, 10, 16, 26, 18, 24, 16, 18, 22, 22, 26, 30, 22, 22, 24, 24, 28, 28, 26, 26, 26, 26, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28],
};
const ECC_BLOCKS = {
  L: [-1, 1, 1, 1, 1, 1, 2, 2, 2, 2, 4, 4, 4, 4, 4, 6, 6, 6, 6, 7, 8, 8, 9, 9, 10, 12, 12, 12, 13, 14, 15, 16, 17, 18, 19, 19, 20, 21, 22, 24, 25],
  M: [-1, 1, 1, 1, 2, 2, 4, 4, 4, 5, 5, 5, 8, 9, 9, 10, 10, 11, 13, 14, 16, 17, 17, 18, 20, 21, 23, 25, 26, 28, 29, 31, 33, 35, 37, 38, 40, 43, 45, 47, 49],
};
const ECL_BITS = { L: 1, M: 0 };

const bit = (x, i) => ((x >>> i) & 1) !== 0;

// Multiplicação no campo de Galois GF(256), polinômio 0x11D
function gfMul(x, y) {
  let z = 0;
  for (let i = 7; i >= 0; i--) {
    z = (z << 1) ^ ((z >>> 7) * 0x11d);
    z ^= ((y >>> i) & 1) * x;
  }
  return z & 0xff;
}

function rsDivisor(degree) {
  const result = new Uint8Array(degree);
  result[degree - 1] = 1;
  let root = 1;
  for (let i = 0; i < degree; i++) {
    for (let j = 0; j < degree; j++) {
      result[j] = gfMul(result[j], root);
      if (j + 1 < degree) result[j] ^= result[j + 1];
    }
    root = gfMul(root, 0x02);
  }
  return result;
}

function rsRemainder(data, divisor) {
  const result = new Uint8Array(divisor.length);
  for (const b of data) {
    const factor = b ^ result[0];
    result.copyWithin(0, 1);
    result[result.length - 1] = 0;
    for (let i = 0; i < divisor.length; i++) result[i] ^= gfMul(divisor[i], factor);
  }
  return result;
}

// Módulos brutos (em bits) disponíveis na versão
function rawDataModules(ver) {
  let result = (16 * ver + 128) * ver + 64;
  if (ver >= 2) {
    const numAlign = Math.floor(ver / 7) + 2;
    result -= (25 * numAlign - 10) * numAlign - 55;
    if (ver >= 7) result -= 36;
  }
  return result;
}

const dataCodewords = (ver, ecl) =>
  Math.floor(rawDataModules(ver) / 8) - ECC_CODEWORDS[ecl][ver] * ECC_BLOCKS[ecl][ver];

function alignPositions(ver) {
  if (ver === 1) return [];
  const numAlign = Math.floor(ver / 7) + 2;
  const step = ver === 32 ? 26 : Math.ceil((ver * 4 + 4) / (numAlign * 2 - 2)) * 2;
  const result = [6];
  for (let pos = ver * 4 + 10; result.length < numAlign; pos -= step) result.splice(1, 0, pos);
  return result;
}

function toUtf8(text) {
  if (typeof TextEncoder !== "undefined") return new TextEncoder().encode(text);
  const s = unescape(encodeURIComponent(text));
  const out = new Uint8Array(s.length);
  for (let i = 0; i < s.length; i++) out[i] = s.charCodeAt(i);
  return out;
}

function buildDataCodewords(bytes, ver, ecl) {
  const bb = [];
  const append = (val, len) => { for (let i = len - 1; i >= 0; i--) bb.push((val >>> i) & 1); };
  append(4, 4);                                   // modo byte
  append(bytes.length, ver < 10 ? 8 : 16);        // contagem de caracteres
  for (const b of bytes) append(b, 8);

  const capBits = dataCodewords(ver, ecl) * 8;
  append(0, Math.min(4, capBits - bb.length));    // terminador
  append(0, (8 - (bb.length % 8)) % 8);           // alinha em byte
  for (let pad = 0xec; bb.length < capBits; pad ^= 0xec ^ 0x11) append(pad, 8);

  const out = new Uint8Array(bb.length / 8);
  bb.forEach((b, i) => { out[i >>> 3] |= b << (7 - (i & 7)); });
  return out;
}

// Divide em blocos, calcula a correção de erro e intercala
function addEccAndInterleave(data, ver, ecl) {
  const numBlocks = ECC_BLOCKS[ecl][ver];
  const eccLen = ECC_CODEWORDS[ecl][ver];
  const rawCodewords = Math.floor(rawDataModules(ver) / 8);
  const numShort = numBlocks - (rawCodewords % numBlocks);
  const shortLen = Math.floor(rawCodewords / numBlocks);
  const divisor = rsDivisor(eccLen);

  const blocks = [];
  for (let i = 0, k = 0; i < numBlocks; i++) {
    const len = shortLen - eccLen + (i < numShort ? 0 : 1);
    const dat = data.slice(k, k + len);
    k += len;
    const ecc = rsRemainder(dat, divisor);
    const blk = Array.from(dat);
    if (i < numShort) blk.push(0);                // preenchimento virtual
    blocks.push(blk.concat(Array.from(ecc)));
  }

  const result = [];
  for (let i = 0; i < blocks[0].length; i++) {
    for (let j = 0; j < blocks.length; j++) {
      if (i !== shortLen - eccLen || j >= numShort) result.push(blocks[j][i]);
    }
  }
  return result;
}

function maskAt(mask, x, y) {
  switch (mask) {
    case 0: return (x + y) % 2 === 0;
    case 1: return y % 2 === 0;
    case 2: return x % 3 === 0;
    case 3: return (x + y) % 3 === 0;
    case 4: return (Math.floor(x / 3) + Math.floor(y / 2)) % 2 === 0;
    case 5: return ((x * y) % 2) + ((x * y) % 3) === 0;
    case 6: return (((x * y) % 2) + ((x * y) % 3)) % 2 === 0;
    default: return (((x + y) % 2) + ((x * y) % 3)) % 2 === 0;
  }
}

// Regras de penalidade da norma (usadas para escolher a melhor máscara)
function penalty(modules, size) {
  let score = 0;
  const runScore = (run) => (run >= 5 ? 3 + (run - 5) : 0);

  for (let y = 0; y < size; y++) {
    let run = 1;
    for (let x = 1; x < size; x++) {
      if (modules[y][x] === modules[y][x - 1]) run++;
      else { score += runScore(run); run = 1; }
    }
    score += runScore(run);
  }
  for (let x = 0; x < size; x++) {
    let run = 1;
    for (let y = 1; y < size; y++) {
      if (modules[y][x] === modules[y - 1][x]) run++;
      else { score += runScore(run); run = 1; }
    }
    score += runScore(run);
  }
  for (let y = 0; y < size - 1; y++) {
    for (let x = 0; x < size - 1; x++) {
      const c = modules[y][x];
      if (c === modules[y][x + 1] && c === modules[y + 1][x] && c === modules[y + 1][x + 1]) score += 3;
    }
  }
  // Padrão 1:1:3:1:1 (parecido com o marcador de posição)
  const pat = [true, false, true, true, true, false, true];
  const hasPattern = (get, i) => {
    for (let k = 0; k < 7; k++) if (get(i + k) !== pat[k]) return false;
    return true;
  };
  for (let y = 0; y < size; y++) {
    for (let x = 0; x <= size - 7; x++) {
      if (hasPattern((i) => modules[y][i], x)) {
        const beforeOk = x < 4 || [0, 1, 2, 3].every((k) => !modules[y][x - 1 - k]);
        const afterOk = x + 7 > size - 5 || [0, 1, 2, 3].every((k) => !modules[y][x + 7 + k]);
        if (beforeOk || afterOk) score += 40;
      }
    }
  }
  for (let x = 0; x < size; x++) {
    for (let y = 0; y <= size - 7; y++) {
      if (hasPattern((i) => modules[i][x], y)) {
        const beforeOk = y < 4 || [0, 1, 2, 3].every((k) => !modules[y - 1 - k][x]);
        const afterOk = y + 7 > size - 5 || [0, 1, 2, 3].every((k) => !modules[y + 7 + k][x]);
        if (beforeOk || afterOk) score += 40;
      }
    }
  }
  let dark = 0;
  for (let y = 0; y < size; y++) for (let x = 0; x < size; x++) if (modules[y][x]) dark++;
  const total = size * size;
  score += Math.floor(Math.abs(dark * 20 - total * 10) / total) * 10;
  return score;
}

/**
 * Gera a matriz do QR Code.
 * @param {string} text conteúdo (normalmente a URL da ficha)
 * @param {"M"|"L"} preferEcl nível de correção preferido
 * @returns {boolean[][]|null} matriz [y][x] ou null se o texto não couber
 */
export function qrMatrix(text, preferEcl = "M") {
  const bytes = toUtf8(String(text ?? ""));
  const levels = preferEcl === "M" ? ["M", "L"] : ["L"];

  let ver = 0, ecl = null;
  for (const lvl of levels) {
    for (let v = 1; v <= 40; v++) {
      const ccBits = v < 10 ? 8 : 16;
      if (Math.ceil((4 + ccBits + bytes.length * 8) / 8) <= dataCodewords(v, lvl)) { ver = v; ecl = lvl; break; }
    }
    if (ver) break;
  }
  if (!ver) return null; // conteúdo grande demais até para a versão 40

  const size = ver * 4 + 17;
  const modules = Array.from({ length: size }, () => new Array(size).fill(false));
  const isFn = Array.from({ length: size }, () => new Array(size).fill(false));
  const setFn = (x, y, dark) => {
    if (x < 0 || y < 0 || x >= size || y >= size) return;
    modules[y][x] = dark;
    isFn[y][x] = true;
  };

  // Linhas de sincronismo
  for (let i = 0; i < size; i++) { setFn(6, i, i % 2 === 0); setFn(i, 6, i % 2 === 0); }

  // Marcadores de posição + separadores
  for (const [fx, fy] of [[3, 3], [size - 4, 3], [3, size - 4]]) {
    for (let dy = -4; dy <= 4; dy++) {
      for (let dx = -4; dx <= 4; dx++) {
        const d = Math.max(Math.abs(dx), Math.abs(dy));
        setFn(fx + dx, fy + dy, d !== 2 && d !== 4);
      }
    }
  }

  // Marcadores de alinhamento
  const align = alignPositions(ver);
  for (let i = 0; i < align.length; i++) {
    for (let j = 0; j < align.length; j++) {
      if ((i === 0 && j === 0) || (i === 0 && j === align.length - 1) || (i === align.length - 1 && j === 0)) continue;
      for (let dy = -2; dy <= 2; dy++) {
        for (let dx = -2; dx <= 2; dx++) setFn(align[j] + dx, align[i] + dy, Math.max(Math.abs(dx), Math.abs(dy)) !== 1);
      }
    }
  }

  const drawFormat = (mask) => {
    const data = (ECL_BITS[ecl] << 3) | mask;
    let rem = data;
    for (let i = 0; i < 10; i++) rem = (rem << 1) ^ ((rem >>> 9) * 0x537);
    const bits = (((data << 10) | rem) ^ 0x5412) & 0x7fff;
    for (let i = 0; i <= 5; i++) setFn(8, i, bit(bits, i));
    setFn(8, 7, bit(bits, 6));
    setFn(8, 8, bit(bits, 7));
    setFn(7, 8, bit(bits, 8));
    for (let i = 9; i < 15; i++) setFn(14 - i, 8, bit(bits, i));
    for (let i = 0; i < 8; i++) setFn(size - 1 - i, 8, bit(bits, i));
    for (let i = 8; i < 15; i++) setFn(8, size - 15 + i, bit(bits, i));
    setFn(8, size - 8, true);
  };
  drawFormat(0); // reserva a área (o valor real é gravado depois da máscara)

  if (ver >= 7) {
    let rem = ver;
    for (let i = 0; i < 12; i++) rem = (rem << 1) ^ ((rem >>> 11) * 0x1f25);
    const bits = (ver << 12) | rem;
    for (let i = 0; i < 18; i++) {
      const b = bit(bits, i), a = size - 11 + (i % 3), c = Math.floor(i / 3);
      setFn(a, c, b);
      setFn(c, a, b);
    }
  }

  // Dados em ziguezague
  const all = addEccAndInterleave(buildDataCodewords(bytes, ver, ecl), ver, ecl);
  let idx = 0;
  for (let right = size - 1; right >= 1; right -= 2) {
    if (right === 6) right = 5;
    for (let vert = 0; vert < size; vert++) {
      for (let j = 0; j < 2; j++) {
        const x = right - j;
        const upward = ((right + 1) & 2) === 0;
        const y = upward ? size - 1 - vert : vert;
        if (!isFn[y][x] && idx < all.length * 8) {
          modules[y][x] = bit(all[idx >>> 3], 7 - (idx & 7));
          idx++;
        }
      }
    }
  }

  // Escolhe a máscara de menor penalidade
  let best = 0, bestScore = Infinity;
  for (let m = 0; m < 8; m++) {
    for (let y = 0; y < size; y++) for (let x = 0; x < size; x++) if (!isFn[y][x] && maskAt(m, x, y)) modules[y][x] = !modules[y][x];
    drawFormat(m);
    const s = penalty(modules, size);
    if (s < bestScore) { bestScore = s; best = m; }
    for (let y = 0; y < size; y++) for (let x = 0; x < size; x++) if (!isFn[y][x] && maskAt(m, x, y)) modules[y][x] = !modules[y][x];
  }
  for (let y = 0; y < size; y++) for (let x = 0; x < size; x++) if (!isFn[y][x] && maskAt(best, x, y)) modules[y][x] = !modules[y][x];
  drawFormat(best);

  return modules;
}
